import { useLayoutEffect } from 'react'
import { classNames, legacyHookAttrs } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { treeStore } from '@/msgdeskRuntime/state'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import type { EditorMode } from '@/msgdeskRuntime/contracts/editorActions'
import { contract } from '../../Editor.helpers'
import { useEditorSection } from '../../EditorTemplateContract'
import { useEditorSurface } from '../../EditorSurfaceContext'
import { NameKeyBlock } from '../NameKey/NameKey'
import { RecipientsBlock } from '../Recipients/Recipients'

export function HeadShort() {
  const { skin } = useEditorSurface()
  return (
    <div {...contract('editor.headShort')} className={classNames('md-editor__head-short', skin.headShort)} hidden aria-hidden="true">
      <div>Name key</div>
      <div>Subject</div>
      <div>Addresses</div>
    </div>
  )
}

export type { EditorMode } from '@/msgdeskRuntime/contracts/editorActions'

// Legacy stacks the head fields in a single column (name key → recipients →
// subject), not the modern 3-column tabbed layout. Rebuilt in flex; the field
// hooks stay addressable for legacy.
export function EditorHeadFields({ editorMode }: { editorMode?: EditorMode }) {
  const { variant, skin } = useEditorSurface()
  const showNameKey = editorMode !== 'new_answer'
  const isMailbox = variant === 'mailbox'
  // Std runs the exclusive compact/extended namekey switch (NameKeyBlock):
  // contract visibility must agree with the mode so the imperative chrome
  // projector (fed by legacy toggle_head_smart at show_form) and React never
  // fight. Mailbox keeps its additive fold — both blocks stay visible.
  const senderSection = useEditorSection('senderOptions')
  const isExtMode = senderSection.state.open ?? false

  useLayoutEffect(() => {
    const setVisible = treeStore.mutate.setContractVisible
    if (typeof setVisible !== 'function') return

    const updates: Array<[string, boolean]> = [
      ['editor.head.nameKey', showNameKey],
      ['editor.smartName.root', showNameKey && (isMailbox || !isExtMode)],
      ['editor.extName.root', showNameKey && (isMailbox || isExtMode)],
      ['editor.head.recipients', true],
      ['editor.head.subject', true],
    ]

    for (const [contractName, visible] of updates) {
      if (treeStore.mutate.hasContract?.(contractName)) setVisible(contractName, visible)
    }
  }, [showNameKey, editorMode, isMailbox, isExtMode])

  return (
    <div {...contract('editor.head')} className={classNames('md-editor__head', skin.head.root)}>
      {showNameKey ? (
        <div {...contract('editor.head.nameKey')} className={classNames('md-editor__head-row', skin.head.nameKeyRow)}>
          {isMailbox ? <span className={skin.head.fromLabel}>From</span> : null}
          <NameKeyBlock />
        </div>
      ) : null}

      <div {...contract('editor.head.recipients')} className={classNames('md-editor__head-row', skin.head.recipientsRow)}>
        <RecipientsBlock />
      </div>

      <div {...contract('editor.head.subject')} className={classNames('md-editor__head-row md-editor__head-subject', skin.head.subjectRow)}>
        <span className={classNames('md-editor__head-label', skin.head.subjectLabel)}>Subject{isMailbox ? '' : ':'}</span>
        <div {...legacyHookAttrs('editor.subject.inputBox', classNames('md-editor__subject-input', skin.head.subjectInputBox))}>
          <input
            className={skin.head.subjectInput}
            name="subject"
            placeholder="Subject"
            type="text"
            onInput={event => dispatchEditorAction({ action: 'set-subject', value: event.currentTarget.value })}
          />
        </div>
        <span {...contract('editor.subject.button')} className={skin.head.compatControl} />
        <span {...contract('editor.subject.span')} className={skin.head.compatControl} />
      </div>
    </div>
  )
}
